import React, { FC, useCallback, useState } from "react";
import { Form } from "antd";
import { Spinner } from "../spinner";
import { MatterCreationOrEditingForm } from "../matter-creation-or-editing-form/matter-creation-or-editing-form";
import {
  ClientFilterData,
  ClientValue,
  ExternalMatterData,
  Matter,
  MatterFormMode,
  MatterNonExternalFieldsPreUpdate,
  MatterTemplate,
  MatterType,
  OnClientSave,
  OnMatterUpdate,
  PracticeArea,
} from "../../data-types";

import styles from "./matter-details-editor.css";

const { ["wrapper"]: wrapper, ["spinner"]: spinner } = styles as any;

type MatterDetailsSharedEditorProps<T extends MatterType, U extends MatterFormMode> = {
  className?: string;
  pmsName: string;
  practiceAreas: PracticeArea[];
  onMatterUpdate: OnMatterUpdate<T, U>;
  feeEarnersCascaderRender: () => React.ReactNode;
};

type MatterDetailsTemplateEditorProps<
  T extends MatterType,
  U extends MatterFormMode
> = MatterDetailsSharedEditorProps<T, U> & {
  clientFilterData?: never;
  externalMatterData?: never;
  onClientSave?: never;
  matter: MatterTemplate;
  client?: never;
  setCurrentClient?: never;
};

type MatterDetailsMatterEditorProps<
  T extends MatterType,
  U extends MatterFormMode
> = MatterDetailsSharedEditorProps<T, U> & {
  clientFilterData: ClientFilterData;
  externalMatterData: ExternalMatterData;
  onClientSave: OnClientSave;
  matter: Matter;
  client?: ClientValue | null;
  setCurrentClient?: (client: ClientValue | undefined | null) => void;
};

type MatterDetailsEditorProps<T extends MatterType, U extends MatterFormMode> = T extends "template"
  ? MatterDetailsTemplateEditorProps<T, U>
  : MatterDetailsMatterEditorProps<T, U>;

type MatterDetailsEditorRenderProps<E extends MatterType, F extends MatterFormMode> = {
  template: E;
  onDelete: () => Promise<void>;
} & MatterDetailsEditorProps<E, F>;

export const MatterDetailsEditor = <T extends MatterType, U extends MatterFormMode>({
  template,
  className,
  externalMatterData,
  practiceAreas,
  onClientSave,
  onMatterUpdate,
  clientFilterData,
  feeEarnersCascaderRender,
  matter,
  pmsName,
  client,
  setCurrentClient,
  onDelete,
}: MatterDetailsEditorRenderProps<T, U>): ReturnType<FC<MatterDetailsEditorRenderProps<T, U>>> => {
  const [loading, setLoading] = useState<boolean>(false);

  const [form] = Form.useForm();

  const onFinish = useCallback(
    <T extends MatterType>(formData: MatterNonExternalFieldsPreUpdate<T, U>) => {
      setLoading(true);
      try {
        onMatterUpdate(formData as any);
      } finally {
        setLoading(false);
      }
    },
    [onMatterUpdate]
  );

  return (
    <div className={`${className} ${wrapper}`}>
      <Spinner className={spinner} show={loading === true} message={"Updating Matter..."}>
        <MatterCreationOrEditingForm
          template={template as MatterType}
          form={form}
          onFinish={onFinish as any}
          matter={matter as any}
          externalMatterData={externalMatterData as ExternalMatterData}
          pmsName={pmsName}
          practiceAreas={practiceAreas}
          onClientSave={onClientSave as OnClientSave}
          clientFilterData={clientFilterData as ClientFilterData}
          feeEarnersCascaderRender={feeEarnersCascaderRender}
          showSave={true}
          showDelete={true}
          onDelete={onDelete}
          mode={MatterFormMode.Edit}
          client={client}
          setCurrentClient={setCurrentClient}
          parentMatterId={matter.parentMatterId ?? undefined}
          allowChangeCurrency={true}
        />
      </Spinner>
    </div>
  );
};
